import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from "lucide-react";
import { submitContact } from "../lib/api";
import { toast } from "sonner";
import { waLink, CONTACTS } from "../lib/whatsapp";
import { useSiteSettings } from "../lib/siteSettings";
import PageHero from "../components/PageHero";

const initialState = { name: "", email: "", phone: "", company: "", subject: "", message: "" };

export default function Contato() {
  const [form, setForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const { settings } = useSiteSettings();

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Preencha nome, e-mail e mensagem.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Mensagem enviada! Entraremos em contato em breve.");
      setForm(initialState);
    } catch {
      toast.error("Não foi possível enviar agora. Tente novamente ou use o WhatsApp.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contato-page" className="bg-white">
      <PageHero
        overline="FALE CONOSCO"
        title="Vamos conversar sobre sua operação."
        subtitle="Preencha o formulário ou fale direto com nosso time pelo WhatsApp — resposta em até 24h úteis."
        image={settings.cover_contato}
      />

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-12">
          {/* FORM */}
          <div className="lg:col-span-7">
            <div className="font-mono-sm text-xs tracking-[0.3em] uppercase text-[#E84E34] mb-3">
              // FORMULÁRIO
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight mb-8">
              Solicite seu orçamento
            </h2>
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Nome *" name="name" value={form.name} onChange={handleChange} testid="contact-name" />
                <Field label="E-mail *" name="email" type="email" value={form.email} onChange={handleChange} testid="contact-email" />
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                <Field label="Telefone / WhatsApp" name="phone" value={form.phone} onChange={handleChange} testid="contact-phone" />
                <Field label="Empresa" name="company" value={form.company} onChange={handleChange} testid="contact-company" />
              </div>
              <Field label="Assunto" name="subject" value={form.subject} onChange={handleChange} testid="contact-subject" />
              <div>
                <label className="block font-mono-sm text-[10px] tracking-[0.25em] uppercase text-[#6B7280] mb-2">
                  Mensagem *
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={6}
                  data-testid="contact-message"
                  className="w-full bg-white border border-[#E5E7EB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm rounded-none resize-none"
                  placeholder="Conte um pouco sobre sua necessidade..."
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="inline-flex items-center gap-3 bg-[#E84E34] text-white font-bold px-8 py-4 uppercase text-xs tracking-[0.2em] hover:bg-[#CF412A] transition-colors disabled:opacity-60"
              >
                {loading ? "Enviando..." : "Enviar mensagem"} <Send size={16} />
              </button>
            </form>
          </div>

          {/* CONTACT INFO */}
          <aside className="lg:col-span-5 space-y-px bg-[#E5E7EB] border border-[#E5E7EB]">
            <InfoBlock icon={MessageCircle} title="WhatsApp">
              {Object.values(CONTACTS).map((c) => (
                <a
                  key={c.phone}
                  href={waLink(`Olá Avantech, falando com ${c.name}.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block hover:text-[#E84E34] transition-colors"
                  data-testid={`whatsapp-${c.name.toLowerCase()}`}
                >
                  {c.display} — {c.name}
                </a>
              ))}
            </InfoBlock>
            <InfoBlock icon={Phone} title="Telefones">
              <div>(54) 3019-8737</div>
              <div>(54) 3019-1380</div>
            </InfoBlock>
            <InfoBlock icon={Mail} title="E-mails">
              <a href="mailto:comercial@avantech.inf.br" className="block hover:text-[#E84E34]">
                comercial@avantech.inf.br
              </a>
              <a href="mailto:eronilde@avantech.inf.br" className="block hover:text-[#E84E34]">
                eronilde@avantech.inf.br
              </a>
            </InfoBlock>
            <InfoBlock icon={MapPin} title="Endereço">
              Rua José Soares de Oliveira, 2490<br />
              Bairro Pio X — Caxias do Sul/RS<br />
              CEP 95034-100
            </InfoBlock>
            <InfoBlock icon={Clock} title="Atendimento">
              Seg a Qui: 7h30 – 12h / 13h – 17h30<br />
              Sex: 7h – 12h / 13h – 16h30
            </InfoBlock>
          </aside>
        </div>
      </section>

      {/* MAP */}
      <section className="bg-[#F9F9F9] pb-20">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
          <div className="border border-[#E5E7EB] aspect-[16/7] overflow-hidden bg-white">
            <iframe
              title="Avantech localização"
              src="https://www.google.com/maps?q=Rua+José+Soares+de+Oliveira+2490+Caxias+do+Sul&output=embed"
              loading="lazy"
              className="w-full h-full border-0"
              data-testid="contact-map"
            />
          </div>
        </div>
      </section>
    </main>
  );
}

const Field = ({ label, name, type = "text", value, onChange, testid }) => (
  <div>
    <label className="block font-mono-sm text-[10px] tracking-[0.25em] uppercase text-[#6B7280] mb-2">
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      data-testid={testid}
      className="w-full bg-white border border-[#E5E7EB] focus:border-[#1A1A1A] outline-none px-4 py-3 text-sm rounded-none"
    />
  </div>
);

const InfoBlock = ({ icon: Icon, title, children }) => (
  <div className="bg-white p-6 md:p-8">
    <div className="flex items-center gap-3">
      <Icon size={20} strokeWidth={1.5} className="text-[#E84E34]" />
      <div className="font-mono-sm text-[10px] tracking-[0.25em] uppercase text-[#6B7280]">
        {title}
      </div>
    </div>
    <div className="mt-3 text-sm text-[#1A1A1A] leading-relaxed">{children}</div>
  </div>
);
